<mat-progress-bar mode="determinate" [value]="progress" />

<h2 mat-dialog-title>Register Shopping</h2>

<mat-dialog-content class="mat-typography">
  <mat-stepper linear #stepper>
    <mat-step [stepControl]="sessionForm.formGroup" [completed]="sessionForm.formGroup.valid">
      <ng-template matStepLabel>
        @if (sessionForm.formGroup.valid) {
          {{ sessionForm.controls.date.value | date: 'mediumDate' }}
          at {{ sessionForm.controls.store.value?.name }}
        } @else {
          Session
        }
      </ng-template>

      <form class="mt-2" [formGroup]="sessionForm.formGroup">
        <div class="row">
          <div class="col-xl-3 col-lg-4 col-md-6">
            <app-date-input [control]="sessionForm.controls.date" label="Date" />
          </div>

          <div class="col-xl-3 col-lg-4 col-md-6">
            <app-number-input [control]="sessionForm.controls.totalPrice" label="Total Price" suffix="kr" />
          </div>

          <div class="col-xl-3 col-lg-4 col-md-6">
            <mat-form-field>
              <mat-label>Shopping Location</mat-label>
              <mat-select [formControl]="sessionForm.controls.store" (selectionChange)="nextStep()">
                @for (option of contextData?.stores; track $index) {
                  <mat-option [value]="option">
                    {{ option.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>
      </form>
    </mat-step>

    <mat-step [stepControl]="productForm.formGroup">
      <ng-template matStepLabel>
        @if (productForm.formGroup.valid) {
          {{ productForm.controls.product.value }}
        } @else {
          Product
        }
      </ng-template>

      <div class="row">
        <div
          class="col-12 mb-3"
          [ngClass]="{ 'col-xxl-7 col-xl-6 col-lg-12': selectedProduct && recentStockLogs.length }">
          <form class="mt-2" [formGroup]="productForm.formGroup">
            <app-grocy-product-select [hideNotInStock]="false" />
          </form>

          @if (selectedProduct) {
            <form [formGroup]="itemForm.formGroup">
              <div class="row">
                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>Amount</mat-label>

                    <input matInput [formControl]="itemForm.controls.amount" type="number" />

                    <span matSuffix class="pe-3">
                      {{ itemForm.quantityUnitName }}
                    </span>

                    <mat-hint>{{ itemForm.newStock }}</mat-hint>
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>{{ itemForm.quantityUnit?.description }} Price</mat-label>

                    <input matInput [formControl]="itemForm.controls.purchaseQuPrice" type="number" />

                    <span matSuffix>kr</span>

                    <mat-hint>
                      {{ itemForm.amount }} * {{ itemForm.purchaseQuPrice | currency: 'SEK' }} =
                      {{ itemForm.fullPrice | currency: 'SEK' }}
                    </mat-hint>
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>
                      {{ itemForm.quantityUnit?.description }}
                      Pant
                    </mat-label>

                    <input matInput [formControl]="itemForm.controls.purchaseQuPant" type="number" />

                    <div matSuffix class="d-flex align-items-center">
                      <span class="me-3">kr</span>
                      <mat-slide-toggle [formControl]="itemForm.controls.pantIncluded">Included</mat-slide-toggle>
                    </div>

                    @if (itemForm.purchaseQuPant > 0 && itemForm.pantIncluded) {
                      <mat-hint>
                        {{ itemForm.amount }} * {{ itemForm.purchaseQuPant | currency: 'SEK' }} =
                        {{ itemForm.totalPant | currency: 'SEK' }}
                      </mat-hint>
                    }
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>Total Discount</mat-label>

                    <input matInput [formControl]="itemForm.controls.totalDiscount" type="number" />

                    <span matSuffix>kr</span>

                    @if (itemForm.totalDiscount > 0) {
                      <mat-hint>
                        {{ itemForm.fullPrice | currency: 'SEK' }}
                        - {{ itemForm.totalDiscount | currency: 'SEK' }} =
                        {{ itemForm.actualPrice | currency: 'SEK' }}
                      </mat-hint>
                    }
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <app-date-input
                    [control]="itemForm.controls.bestBeforeDate"
                    label="Best Before"
                    [toggleControl]="itemForm.controls.neverOverdue" />
                </div>
              </div>
            </form>
          }
        </div>

        @if (selectedProduct && recentStockLogs.length) {
          <div class="col-xxl-5 col-xl-6 col-lg-12">
            <div class="mt-3 ms-3">
              <app-grocy-recent-stock-logs />
            </div>
          </div>
        }
      </div>
    </mat-step>
  </mat-stepper>

  @if (dataSource.data.length) {
    <div class="table-container table-container-full-height p-4">
      <h3>Registered Purchases</h3>
      <table mat-table [dataSource]="dataSource">
        <ng-container matColumnDef="product">
          <th mat-header-cell *matHeaderCellDef>Product</th>
          <td mat-cell *matCellDef="let row">
            {{ row.productName }}
          </td>
          <td mat-footer-cell *matFooterCellDef>
            <b>Total</b>
          </td>
        </ng-container>

        <ng-container matColumnDef="breakdown">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            @if (row.amount > 1) {
              <div class="registered-purchase-details">{{ row.amount }} * {{ row.unitPrice | currency: 'SEK' }}</div>
            }

            @if (row.totalDiscount > 0) {
              <div>&nbsp;</div>
            }

            @if (row.unitPant > 0) {
              <div class="registered-purchase-details">
                {{ row.amount }} * {{ row.unitPant | currency: 'SEK' }} (PANT)
              </div>
            }
          </td>
          <td mat-footer-cell *matFooterCellDef></td>
        </ng-container>

        <ng-container matColumnDef="price">
          <th mat-header-cell *matHeaderCellDef>Price</th>
          <td mat-cell *matCellDef="let row">
            <b>{{ row.fullPrice | currency: 'SEK' }}</b>

            @if (row.totalDiscount > 0) {
              <div class="registered-purchase-discount">- {{ row.totalDiscount | currency: 'SEK' }}</div>
            }

            @if (row.totalPant > 0) {
              <div class="registered-purchase-pant">+ {{ row.totalPant | currency: 'SEK' }}</div>
            }
          </td>
          <td mat-footer-cell *matFooterCellDef>
            <b>{{ priceProgress | currency: 'SEK' }}</b>
          </td>
        </ng-container>

        <ng-container matColumnDef="undo">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            <button mat-icon-button (click)="undoTransaction(row.transactionId)" [disabled]="isSubmitting">
              <mat-icon>undo</mat-icon>
            </button>
          </td>
          <td mat-footer-cell *matFooterCellDef></td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columns"></tr>
        <tr mat-row *matRowDef="let row; columns: columns"></tr>
        <tr mat-footer-row *matFooterRowDef="columns"></tr>
      </table>
    </div>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <div class="row mt-3">
    <div class="col text-end">
      @if (stepper.selectedIndex === 1) {
        <button
          mat-raised-button
          color="primary"
          (click)="submit()"
          [disabled]="itemForm.formGroup.invalid || isSubmitting">
          <mat-icon>done</mat-icon>
          Register
        </button>
      }

      @if (!finishShoppingClicked) {
        <button mat-raised-button (click)="finishShoppingConfirmation()">
          <mat-icon>clear</mat-icon>
          Finish Shopping
        </button>
      }

      @if (itemForm) {
        <app-open-form-validation-errors-dialog [formGroup]="itemForm.formGroup" />
      }

      @if (finishShoppingClicked) {
        <button mat-raised-button mat-dialog-close (click)="finishShopping()" color="warn">
          <mat-icon>warning</mat-icon>
          Are you sure?
        </button>
      }

      <button mat-raised-button mat-dialog-close>
        <mat-icon>close</mat-icon>
        Close
      </button>
    </div>
  </div>
</mat-dialog-actions>
